/* The catalogue keeps arriving as the shopper reaches it.
 *
 * Progressive enhancement over a real paginated link: with JS the link is a URL to fetch, without it
 * the link is how you reach page two. Nothing here is required for the page to work.
 *
 * TWO THINGS THAT LOOK LIKE DETAIL AND ARE NOT:
 *
 * 1. THE OBSERVED ELEMENT IS NEVER REPLACED. The first version of this file swapped the whole
 *    "more" block for the next page's copy — and IntersectionObserver kept watching the detached
 *    node, which can never intersect anything again. It loaded page 2 and stalled there forever,
 *    silently: no error, no failure flag, and the link still worked when clicked, so it read as
 *    fine. Only the href moves now; the sentinel stays put and stays observed.
 *
 * 2. IO REPORTS TRANSITIONS, NOT STATES — and this sentinel's steady state is "visible". It sits at
 *    the very bottom of the page, so once the shopper reaches the end it is *continuously*
 *    intersecting: IO delivers one entry on the way in and then nothing, ever, no matter how much
 *    they scroll. Measured: stalled at 96 tiles with the sentinel 60px above the fold and no
 *    callback pending. A post-load geometry re-check is not enough on its own either, because it
 *    runs before layout settles (measured 2220px against an 1800px threshold, one frame after the
 *    append, for a sentinel that ended up at 940px).
 *
 *    So the trigger is a rAF-throttled listener that asks "where is it NOW" instead of waiting for
 *    an edge. IO stays as the cheap first trigger; this is the backstop that makes it correct. */
const MARGIN = 800

/* The theme already owns one polite live region; this module borrows it rather than inventing a
   second. Strings come from the DOM (published per-locale by theme.liquid) — this is a bilingual
   store and a hardcoded English sentence read aloud on an Arabic page is a defect the theme has
   shipped once already. */
const LIVE = document.getElementById('opensku-live')
const say = (msg) => { if (LIVE && msg) LIVE.textContent = msg }
const STR = document.body?.dataset || {}

const region = document.querySelector('[data-endless]')
const grid = region?.querySelector('[data-endless-grid]')
const more = region?.querySelector('[data-endless-more]')

if (region && grid && more) {
  let loading = false
  let failed = false
  let done = false

  const link = () => more.querySelector('[data-endless-next]')

  /* FETCH THE SECTION, NOT THE PAGE.
   *
   * The <a> has to point at a real page (`/?page=3`) so it works with JavaScript off — that is the
   * whole progressive-enhancement contract. But fetching that URL re-renders the ENTIRE home page
   * server-side just to keep 24 tiles: the aisle grid, five product shelves and the brand rail are
   * all built, sent, parsed and thrown away. Measured: 550,597 bytes per page against 116,999 for
   * the section alone — 79% of every fetch was discarded, roughly 4.7MB over a full browse, on a
   * store whose shoppers are on mobile data in Amman.
   *
   * Shopify's Section Rendering API returns just this section for the same query. The id comes from
   * Liquid rather than being hardcoded, because it carries the template hash and changes per theme. */
  const sectionId = region.dataset.sectionId
  const fetchUrl = (href) => {
    const u = new URL(href, location.href)
    if (sectionId) u.searchParams.set('section_id', sectionId)
    return u.toString()
  }

  const io = new IntersectionObserver(
    (entries) => { if (entries.some((e) => e.isIntersecting)) load() },
    /* rootMargin so a page is already in flight before the shopper reaches the end of the last one —
       the point is that the catalogue never appears to stop. */
    { rootMargin: `${MARGIN}px 0px` },
  )

  async function load() {
    if (!region.isConnected) { done = true; stop(); return }
    const a = link()
    if (loading || failed || done || !a) return
    loading = true
    region.setAttribute('data-loading', '')
    region.setAttribute('aria-busy', 'true')
    try {
      const res = await fetch(fetchUrl(a.href), { headers: { 'X-Requested-With': 'fetch' } })
      if (!res.ok) throw new Error(String(res.status))
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html')

      /* Take the tiles from the NEXT page's own endless grid, not from any `.grid` on the page — a
         collection template also carries `.grid`, and appending the wrong one would put a
         department's products under a heading that claims the whole catalogue. */
      const incoming = doc.querySelector('[data-endless-grid]')
      const tiles = incoming ? [...incoming.children] : []
      if (!tiles.length) throw new Error('no tiles in response')
      grid.append(...tiles)
      /* Without this the document silently grows under a screen-reader user, who has no way to know
         more arrived and reasonably concludes the catalogue ended at tile 24 of 278. */
      say((STR.strLoadedMore || '').replace('{{ count }}', String(tiles.length)))

      /* THE APPENDED TILES ARE BLANK SLATES.
       *
       * <opensku-qty> reads its quantity in connectedCallback — and product-tile.liquid renders no
       * `quantity` attribute, so every appended stepper initialises to 0 and shows "Add" for a
       * product the shopper already has three of. It only corrects on the next `opensku:cart`
       * broadcast, and nothing broadcasts after an append. Branch-aware accessible names have the
       * same shape: syncAddLabels() runs once at module load, over tiles that existed then.
       *
       * An EVENT rather than an import, for the reason opensku-cart.js documents at length: this
       * file is loaded with a `?v=` hash, so importing it resolves to a second module instance and a
       * duplicate customElements.define. */
      document.dispatchEvent(new CustomEvent('opensku:dom-added', { detail: { count: tiles.length } }))

      /* Only the href moves. See note 1 above. */
      const nextHref = doc.querySelector('[data-endless-next]')?.getAttribute('href')
      if (nextHref) {
        a.href = nextHref
      } else {
        /* THE LAST PAGE. Removing the anchor is right — there is nothing further to load — but the
           keyboard shopper who just pressed Enter on it is standing on that element, and removing
           document.activeElement resets focus to <body>. Their next Tab restarts at the skip link,
           throwing them back over the header and every aisle in the rail. Hand focus to a real
           end-of-catalogue message instead of deleting the ground they are on. */
        const held = document.activeElement === a
        const end = document.createElement('p')
        end.className = 'allcat__end'
        end.tabIndex = -1
        end.textContent = STR.strEndOfCatalogue || ''
        a.replaceWith(end)
        if (held) end.focus()
        say(end.textContent)
        done = true
        stop()
      }
    } catch {
      /* Stop asking rather than hammering a failing endpoint. The link is still there and still
         works as a plain link, which is the whole reason it is a real <a>. */
      failed = true
      stop()
      region.setAttribute('data-endless-failed', '')

      /* Say it on the page as well as in the live region. The click guard above now lets the link
         navigate, so this sentence explains a control that really does still work. */
      const msg = STR.strLoadFailed || ''
      if (msg && more && !more.querySelector('.allcat__fail')) {
        const p = document.createElement('p')
        p.className = 'allcat__fail'
        p.textContent = msg
        more.prepend(p)
      }
      say(msg)
    } finally {
      loading = false
      region.removeAttribute('data-loading')
      region.setAttribute('aria-busy', 'false')
    }

    /* Layout has to settle before the sentinel's new position means anything — see note 2. A task,
       not a frame: rAF does not run in a hidden tab, and a shopper who switches tabs mid-load should
       not come back to a catalogue that quietly stopped. */
    if (!done && !failed) setTimeout(check, 0)
  }

  /* The state test. Cheap: one getBoundingClientRect, and only once per frame. */
  function check() {
    if (loading || failed || done) return
    if (more.getBoundingClientRect().top < innerHeight + MARGIN) load()
  }

  let ticking = false
  function onScroll() {
    if (ticking) return
    ticking = true
    /* setTimeout, NOT requestAnimationFrame — and this is the second time this trap has been walked
       into in this theme. rAF does not run in a hidden tab. The flag that suppresses duplicate work
       is cleared inside the callback, so one scroll in a backgrounded tab latches `ticking` true
       forever and the loader deadlocks permanently: not "pauses until you come back", dead. Measured
       here with document.hidden true — rAF never fired and the catalogue froze at page 1.
       100ms is well inside what a scroll needs, and it runs wherever the shopper is. */
    setTimeout(() => { ticking = false; check() }, 100)
  }

  function stop() {
    io.disconnect()
    removeEventListener('scroll', onScroll)
    removeEventListener('resize', onScroll)
  }

  io.observe(more)
  addEventListener('scroll', onScroll, { passive: true })
  addEventListener('resize', onScroll, { passive: true })

  /* The link keeps working as a link if it is reached before the observer fires, or if the shopper
     tabs to it — but it should load in place rather than navigate. */
  region.addEventListener('click', (e) => {
    const a = e.target.closest('[data-endless-next]')
    if (!a || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return
    /* THE GUARD GOES ABOVE preventDefault, not below it. The catch block above says the link "still
       works as a plain link, which is the whole reason it is a real <a>" — that was false while this
       handler cancelled the click unconditionally and then called a load() that early-returns on
       `failed`. The shopper on a flaky connection tapped "Load more", got nothing, tapped again, got
       nothing: an inert control with no error. Once the loader has given up, let the browser do what
       the markup says and navigate to page N. */
    if (failed || done) return
    e.preventDefault()
    load()
  })
}
